import React, { useState } from "react";
import {
  Layout as AntLayout,
  Menu,
  Avatar,
  Dropdown,
  Space,
  Typography,
} from "antd";
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  DashboardOutlined,
  TeamOutlined,
  MedicineBoxOutlined,
  UserOutlined,
  FileTextOutlined,
  LogoutOutlined,
  SettingOutlined,
  BarChartOutlined,
  ShoppingCartOutlined,
  ExperimentOutlined,
  AuditOutlined,
} from "@ant-design/icons";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { RootState } from "../../store";
import { logout } from "../../store/slices/authSlice";
import authService from "../../services/authService";

const { Header, Sider, Content } = AntLayout;
const { Text } = Typography;

const SIDER_WIDTH = 212;
const SIDER_COLLAPSED_WIDTH = 64;
const HEADER_HEIGHT = 48;

interface LayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<LayoutProps> = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const { user, pharmacyName } = useSelector((state: RootState) => state.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const role = user?.role || "staff";
  const siderWidth = collapsed ? SIDER_COLLAPSED_WIDTH : SIDER_WIDTH;
  const brandName = pharmacyName || "Pharmacy";
  const brandShort = brandName
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 3)
    .toUpperCase() || "PH";

  const handleLogout = () => {
    dispatch(logout());
    authService.logout();
    navigate("/login");
  };

  const allMenuItems = [
    {
      key: "/dashboard",
      icon: <DashboardOutlined />,
      label: "Dashboard",
      roles: ["admin", "manager", "staff"],
    },
    {
      key: "/medicines",
      icon: <ExperimentOutlined />,
      label: "Medicines",
      roles: ["admin", "manager", "staff"],
    },
    {
      key: "/purchases",
      icon: <ShoppingCartOutlined />,
      label: "Purchases",
      roles: ["admin", "manager"],
    },
    {
      key: "/stock",
      icon: <MedicineBoxOutlined />,
      label: "Stock",
      roles: ["admin", "manager", "staff"],
    },
    {
      key: "/patients",
      icon: <UserOutlined />,
      label: "Patients",
      roles: ["admin", "manager", "staff"],
    },
    {
      key: "/billing",
      icon: <FileTextOutlined />,
      label: "Billing",
      roles: ["admin", "manager", "staff"],
    },
    {
      key: "/suppliers",
      icon: <TeamOutlined />,
      label: "Suppliers",
      roles: ["admin", "manager", "staff"],
    },
    {
      key: "/reports",
      icon: <BarChartOutlined />,
      label: "Reports",
      roles: ["admin", "manager"],
    },
    {
      key: "/settings",
      icon: <SettingOutlined />,
      label: "Settings",
      roles: ["admin", "manager"],
    },
    {
      key: "/users",
      icon: <AuditOutlined />,
      label: "Users",
      roles: ["admin"],
    },
  ];

  const menuItems = allMenuItems
    .filter((item) => item.roles.includes(role))
    .map(({ key, icon, label }) => ({ key, icon, label }));

  const userMenuItems = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: "Profile",
      onClick: () => navigate("/profile"),
    },
    ...(role === "admin" || role === "manager"
      ? [
          {
            key: "settings",
            icon: <SettingOutlined />,
            label: "Settings",
            onClick: () => navigate("/settings"),
          },
        ]
      : []),
    { type: "divider" as const },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "Logout",
      onClick: handleLogout,
    },
  ];

  return (
    <AntLayout style={{ height: "100vh", overflow: "hidden" }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        width={SIDER_WIDTH}
        collapsedWidth={SIDER_COLLAPSED_WIDTH}
        style={{
          overflow: "auto",
          height: "100vh",
          position: "fixed",
          left: 0,
          top: 0,
          bottom: 0,
          zIndex: 100,
          background: "#0f1c2e",
        }}
      >
        <div
          style={{
            height: HEADER_HEIGHT,
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "flex-start",
            padding: collapsed ? 0 : "0 16px",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            flexShrink: 0,
          }}
        >
          <Text
            strong
            style={{
              color: "#fff",
              fontSize: collapsed ? 13 : 15,
              letterSpacing: collapsed ? 0 : 0.2,
              whiteSpace: "nowrap",
            }}
          >
            {collapsed ? brandShort : brandName}
          </Text>
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[
            (() => {
              const seg = location.pathname.split("/").filter(Boolean)[0];
              return seg ? `/${seg}` : "/dashboard";
            })(),
          ]}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
          style={{
            borderInlineEnd: 0,
            background: "transparent",
            padding: "8px 0",
          }}
        />
      </Sider>

      <AntLayout
        style={{
          marginLeft: siderWidth,
          height: "100vh",
          overflow: "hidden",
          transition: "margin-left 0.2s",
          background: "#f4f6f8",
        }}
      >
        <Header
          style={{
            padding: "0 12px",
            background: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            height: HEADER_HEIGHT,
            lineHeight: `${HEADER_HEIGHT}px`,
            position: "sticky",
            top: 0,
            zIndex: 99,
            width: "100%",
            borderBottom: "1px solid #e8ecf0",
            flexShrink: 0,
          }}
        >
          {React.createElement(
            collapsed ? MenuUnfoldOutlined : MenuFoldOutlined,
            {
              style: { fontSize: 16, cursor: "pointer", color: "#334155" },
              onClick: () => setCollapsed(!collapsed),
            },
          )}
          <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
            <Space size={8} style={{ cursor: "pointer" }}>
              <Avatar size={28} icon={<UserOutlined />} style={{ background: "#0d7a6f" }} />
              <Text style={{ fontSize: 13, color: "#334155" }}>
                {user?.name}
                <Text type="secondary" style={{ fontSize: 12, marginLeft: 6 }}>
                  {role}
                </Text>
              </Text>
            </Space>
          </Dropdown>
        </Header>

        {/* Direct content — no nested white box / margin frame */}
        <Content
          className="app-content"
          style={{
            margin: 0,
            padding: "12px 14px",
            background: "#f4f6f8",
            overflow: "auto",
            height: `calc(100vh - ${HEADER_HEIGHT}px)`,
          }}
        >
          {children}
        </Content>
      </AntLayout>
    </AntLayout>
  );
};

export default MainLayout;
